import { LocalDateSchema } from '@idc-forms/schemas/primitives/time'
import { z } from 'zod'
import type { IGroup } from '@/features/groups/models/group'
import type { IState } from '@/features/states/models/state.schema'
import type { IUser } from '@/features/users/models/user'
import type { IWorkflow } from '@/features/workflows/models/workflow.schema'
import type { EnumRiskLevel } from '@/shared/catalogs/risk-level'
import type { EnumPersonClient } from '@/shared/catalogs/type-person'
import { MESSAGES_VALIDATE } from '@/shared/constants/messages'
import type { Place } from '@/shared/schemas/place'
import type { Contract } from './client'

export interface IClient {
  typePerson: EnumPersonClient
  group?: string | null
  advisor?: string | null
  signed: boolean
  contract: Contract
  place: Place
  company: string
  agency: string
  riskLevel?: EnumRiskLevel
}

export interface IClientFetch {
  _id: string
  id: string
  name: string
  nit: string
  identification?: string
  email: string | string[]
  code: number
  typePerson: EnumPersonClient
  group: IGroup | null
  advisor: IUser | null
  workflow?: IWorkflow | null
  state: {
    index: number | null
    current: string
    isRejected?: boolean
    stateAt: string
    overdue?: boolean
  }
  currentState: IState
  stateAt: string
  history: Array<{
    nextState: IState
    previousState: IState | null
    indexNextState: number | null
    indexPreviousState: number | null
    isRejected?: boolean
    startDate: string
    endDate: string | null
    previousWorkflow: IWorkflow | null
    nextWorkflow: IWorkflow | null
    comment: string
    updatedBy: IUser
  }>
  signed: boolean
  contract: Contract
  place: Place
  company: string
  agency: string
  createdBy: IUser
  updatedBy: IUser | null
  createdAt: string
  updatedAt: string
  totalHistory: number
  photo: string | null
  riskLevel?: EnumRiskLevel
  /** Hay un flujo de portal activo (no terminal) → el form interno va en solo lectura. */
  hasActiveFlow?: boolean
  /** Estado del flujo activo (p.ej. "under_review") para el copy del banner. */
  activeFlowStatus?: string | null
}

export interface IClientHistory {
  _id: string
  startDate: string
  endDate: string
  workflow: string
}

export const CLIENT_SCHEMA = {
  _ID: '_id',
  ID: 'id',
  CODE: 'code',
  TYPE_PERSON: 'typePerson',
  GROUP: 'group',
  ADVISOR: 'advisor',
  WORKFLOW: 'workflow',
  STATE: 'state',
  SIGNED: 'signed',
  CONTRACT: 'contract', // LINKED SCHEMA TO CONTRACT_SCHEMA
  PLACE: 'place',
  COMPANY: 'company',
  AGENCY: 'agency',
  INACTIVE: 'inactive',
  INACTIVE_AT: 'inactiveAt',
  CREATED_BY: 'createdBy',
  UPDATED_BY: 'updatedBy',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  RISK_LEVEL: 'riskLevel'
} as const

export const NAME_CLIENT_SCHEMA = {
  id: 'ID',
  code: 'Código',
  typePerson: 'Tipo de persona',
  group: 'Grupo',
  advisor: 'Asesor',
  workflow: 'Flujo de trabajo',
  state: 'Estado',
  signed: 'Firmado',
  contract: 'Contrato',
  place: 'Lugar',
  company: 'Empresa',
  agency: 'Agencia',
  inactive: 'Inactivo',
  inactiveAt: 'Inactivo en',
  createdBy: 'Creado por',
  updatedBy: 'Actualizado por',
  createdAt: 'Creado en',
  updatedAt: 'Actualizado en',
  riskLevel: 'Nivel de riesgo'
} as const

export const VALIDATE_CLIENT = z.object({
  [CLIENT_SCHEMA.COMPANY]: z.string().min(1, MESSAGES_VALIDATE.REQUIRED),
  [CLIENT_SCHEMA.AGENCY]: z.string().min(1, MESSAGES_VALIDATE.REQUIRED)
})

export const CONTRACT_SCHEMA = {
  // LINKED SCHEMA TO CLIENT_SCHEMA
  START_AT: 'startAt',
  UPDATE_AT: 'updateAt',
  END_AT: 'endAt'
} as const

export const VALIDATE_CONTRACT = z.object({
  // El servidor la recibe como `contract.startDate` y la valida con `LocalDateSchema`: un
  // `YYYY-MM-DD` real, año de cuatro dígitos. El input `date` deja tipear `20255-10-02`, que
  // pasaba con un `min(1)` y rebotaba en el alta como un 400 sin campo.
  [CONTRACT_SCHEMA.START_AT]: z
    .string()
    .min(1, MESSAGES_VALIDATE.REQUIRED)
    .refine((value) => LocalDateSchema.safeParse(value).success, MESSAGES_VALIDATE.INVALID_DATE),
  [CONTRACT_SCHEMA.UPDATE_AT]: z.string(),
  [CONTRACT_SCHEMA.END_AT]: z.string()
})

export enum EnumActionsModifyClient {
  update = 'update',
  delete = 'delete'
}

// Modify Advisor Schema
export interface IModifyAdvisor {
  action: EnumActionsModifyClient
  clients: string[]
  advisor?: string
}

export const MODIFY_ADVISOR_SCHEMA = {
  ACTION: 'action',
  CLIENTS: 'clients',
  ADVISOR: 'advisor'
}

export const VALIDATION_MODIFY_ADVISOR = z.object({
  [MODIFY_ADVISOR_SCHEMA.ACTION]: z.string().min(1, MESSAGES_VALIDATE.REQUIRED),
  [MODIFY_ADVISOR_SCHEMA.CLIENTS]: z.array(z.string()),
  [MODIFY_ADVISOR_SCHEMA.ADVISOR]: z.string().min(1, MESSAGES_VALIDATE.REQUIRED)
})

// Modify Group Schema
export interface IModifyGroup {
  action: EnumActionsModifyClient
  clients: string[]
  group?: string
}

export const MODIFY_GROUP_SCHEMA = {
  ACTION: 'action',
  CLIENTS: 'clients',
  GROUP: 'group'
}

export const VALIDATION_MODIFY_GROUP = z.object({
  [MODIFY_GROUP_SCHEMA.ACTION]: z.string().min(1, MESSAGES_VALIDATE.REQUIRED),
  [MODIFY_GROUP_SCHEMA.CLIENTS]: z.array(z.string()),
  [MODIFY_GROUP_SCHEMA.GROUP]: z.string().min(1, MESSAGES_VALIDATE.REQUIRED)
})

// NAMES PROPERTIES
export const NAME_PROPERTIES_CONTRACT_SCHEMA = {
  START_AT: 'Fecha de inicio de relación',
  UPDATE_AT: 'Fecha de actualización',
  END_AT: 'Fecha de fin de relación'
}

export const NAME_PROPERTIES_CLIENT_SCHEMA = {
  COMPANY: 'Empresa',
  AGENCY: 'Agencia'
}
